<template>
  <v-dialog
    :value="dialog"
    persistent
    :retain-focus="false"
    transition="fade-transition"
    fullscreen
    class="p-contract-edit-dialog"
    @keydown.esc="escape(closeDialog)"
  >
    <v-toolbar
      color="primaryBgr"
      dark
    >
      <p-btn-icon type="close" btn-class="mr-4" tabindex="3" @click="escape(closeDialog)"></p-btn-icon>
      <v-toolbar-title>{{ $t('contract.editContract') }}</v-toolbar-title>
    </v-toolbar>
    <v-card>
      <v-form ref="form" @submit.stop.prevent>
        <v-container>
          <v-row class="mb-4">
            <v-col cols="4" sm="12" md="6" lg="4" xl="3" class="text-left">
              <v-text-field
                type="text"
                v-model.trim="editedContract.title"
                :label="$t('contract.edit.title')"
                :rules="rules.title"
                prepend-icon="mdi-card-text-outline"
                tabindex="1"
                clearable
                autofocus
              ></v-text-field>

              <contract-signers-select
                v-model="editedContract.signers"
                :items="contactsList"
                :label="$t('contract.edit.signers')"
                prepend-icon="mdi-account-circle-outline"
                tabindex="1"
              ></contract-signers-select>

              <v-select
                v-model="editedContract.providerId"
                :items="signingProviders"
                :label="$t('contract.edit.provider')"
                item-value="id"
                item-text="name"
                prepend-icon="mdi-clipboard-account-outline"
                tabindex="1"
                clearable
              ></v-select>
            </v-col>
            <v-col cols="2">
              <v-btn
                color="primary"
                class="mt-4 hidden-sm-and-down"
                :disabled="!isModelChanged()"
                tabindex="2"
                @click="submit"
              >
                {{ $t('save') }}
              </v-btn>
            </v-col>
          </v-row>
          <ejs-documenteditorcontainer
            v-if="editedContract.contractTemplateId && ejsReady"
            ref="editor"
            :enableToolbar="true"
            :serviceUrl="editorServiceUrl"
            :documentName="editedContract.title"
            :toolbarItems="editorToolbarItems"
            height="700px"
            tabindex="1"
            @created="onEditorCreated"
            @toolbarClick="onEditorToolbarClick"
            @contentChange="onEditorContentChange"
          ></ejs-documenteditorcontainer>
          <v-row class="mb-4" v-if="!editedContract.contractTemplateId">
            <v-col cols="4" sm="12" md="6" lg="4" xl="3" class="text-center">
              <v-btn
                rounded
                class="p-btn-rounded mr-1"
                @click="download">
                <span>{{ $t('contract.download') }}</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </v-card>
  </v-dialog>
</template>


<script>
import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'

import { DocumentEditorContainerPlugin, Toolbar } from '@syncfusion/ej2-vue-documenteditor'

import PBtnIcon from '@/components/PBtnIcon'

import ContractSignersSelect from './ContractSignersSelect'

import documentEditorMixin from '@/mixins/documentEditor.mixin'
import snackbarMixin from '@/mixins/snackbar.mixin'
import objectMixin from '@/mixins/object.mixin'
import dialogMixin from '@/mixins/dialog.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'

Vue.use(DocumentEditorContainerPlugin)


export default {
  provide: {
    DocumentEditorContainer: [Toolbar]
  },


  components: {
    PBtnIcon,
    ContractSignersSelect
  },


  mixins: [
    documentEditorMixin,
    snackbarMixin,
    objectMixin,
    dialogMixin,
    validationMixin,
    unsavedChangesMixin
  ],


  props: {
    contract: Object
  },


  data () {
    return {
      editorServiceUrl: process.env.VUE_APP_DOCUMENT_EDITOR_API_URL,
      editorCreated: false,
      contractLoaded: false,
      editedContract: {},
      ejsReady: false
    }
  },


  computed: {
    ...mapGetters('signingProvider', ['signingProviders']),
    ...mapGetters('contact', ['shortContactsForProject']),
    ...mapGetters('project', ['currentProjectId']),

    editorToolbarItems () {
      const docxItem = this.getToolbarItem(this.toolbarItemId.docx)
      const pdfItem = this.getToolbarItem(this.toolbarItemId.pdf)

      return ['New', 'Open', 'Separator', docxItem, pdfItem, 'Separator', 'Undo', 'Redo', 'Separator', 'Image', 'Table', 'Hyperlink', 'TableOfContents', 'Separator', 'Header', 'Footer', 'PageSetup', 'PageNumber', 'Break', 'Separator', 'Find']
    },

    contactsList () {
      return this.shortContactsForProject(this.currentProjectId)
    },

    rulesToApply () {
      const rule = this.rule

      return {
        title: [rule.required()]
      }
    }
  },


  methods: {
    ...mapActions('contract', ['putContract', 'loadContractById']),
    ...mapActions('changes', ['removeFromChanges']),

    download () {
      this.$emit('download')
    },

    async onDialogOpen () {
      if (this.contract) {
        this.removeFromChanges(this.contract)
      }
      try {
        this.showSnackbarProcessing()
        this.contractLoaded = false
        this.editedContract = this.getObjectCopy(this.contract)
        this.resetValidation()

        const contract = await this.loadContractById(this.contract)
        this.editedContract = this.getObjectCopy(contract)
        this.contractLoaded = true
        this.resetValidation()
        this.hideSnackbar()

        this.prepareEditor()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    async prepareEditor () {
      this.ejsReady = false
      await Vue.nextTick()
      this.ejsReady = true
      await Vue.nextTick()
      this.resetEditorSize()
      if (this.editorCreated) {
        this.setEditorContent()
      }
    },

    removeEditor () {
      this.ejsReady = false
    },

    onEditorCreated () {
      this.editorCreated = true

      if (this.contractLoaded) {
        this.setEditorContent()
      }
    },

    onEditorContentChange () {
      this.updateEditedContractBody()
    },

    async onEditorToolbarClick (args) {
      try {
        switch (args.item.id) {
          case this.toolbarItemId.pdf: {
            await this.saveAsPdf(this.$refs.editor, this.editedContract.title || 'contract')
            break
          }
          case this.toolbarItemId.docx: {
            await this.saveAsDocx(this.$refs.editor, this.editedContract.title || 'contract')
            break
          }
        }
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    setEditorContent () {
      if (!this.$refs?.editor) return

      this.$refs.editor.ej2Instances.documentEditor.open(this.editedContract.body)

      this.updateEditedContractBody()
      this.initWatchedModel('editedContract')
    },

    updateEditedContractBody () {
      this.editedContract.body = this.$refs.editor.ej2Instances.documentEditor.serialize()
    },

    async submit () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged()) return

      try {
        this.showSnackbarProcessing()

        await this.putContract(this.editedContract)

        this.showSnackbarSuccess(this.$t('contract.contractUpdated'))
        this.closeDialog()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.removeEditor()
      }
    }
  }
}
</script>
